import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Chip,
  Checkbox,
  FormControlLabel,
  Stack,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';
import { supabase } from './supabaseClient';
import { openLegalDoc } from './legalDocUrl';

import { prettyStatus } from './labels';
const AGREEMENT_TYPE = 'independent_contractor_agreement';

// Reads a File as a base64 string (without the data: URL prefix) for
// sending to the update_template edge function action as JSON.
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || '';
      const commaIndex = result.indexOf(',');
      resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// 'not_sent' is synthetic — it has no stylist_agreements row at all, so it
// can't be a real value of the status column. It represents every stylist
// the send-on-signup flow hasn't reached (or reached before this flow
// existed).
const STATUS_OPTIONS = ['all', 'not_sent', 'sent', 'signed', 'failed', 'viewed', 'declined', 'voided', 'draft'];

export default function LegalCompliancePage() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [sendingFor, setSendingFor] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  // Agreement template editor (admin-editable text and/or an uploaded
  // master PDF, used by stylist-legal-agreement's send/sign flow instead
  // of the hardcoded placeholder text).
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateVersion, setTemplateVersion] = useState('1.0');
  const [templateBodyText, setTemplateBodyText] = useState('');
  const [templatePdfUrl, setTemplatePdfUrl] = useState(null);
  const [templateFile, setTemplateFile] = useState(null);
  const [templateClearPdf, setTemplateClearPdf] = useState(false);

  const showSnack = (message, severity = 'success') => setSnack({ open: true, message, severity });

  const fetchLegal = async () => {
    setLoading(true);
    try {
      // Anchor on every stylist, not on stylist_agreements — the old query
      // started from stylist_agreements and joined outward, so a stylist
      // with zero agreement rows (i.e. every stylist before the
      // send-on-signup flow existed, and any stylist a send genuinely
      // failed for) was structurally invisible on this page regardless of
      // their real compliance status.
      const [{ data: stylists, error: stylistsError }, { data: agreements }, { data: compliance }] = await Promise.all([
        supabase
          .from('Profiles')
          .select('id, first_name, last_name, email, phone_number')
          .eq('user_type', 'Stylist'),
        supabase
          .from('stylist_agreements')
          .select('*')
          .order('created_at', { ascending: false }),
        supabase
          .from('stylist_compliance_status')
          .select('stylist_id, agreement_status, identity_status, payout_status, updated_at'),
      ]);
      if (stylistsError) throw stylistsError;

      // Most recent agreement per stylist (agreements already sorted desc).
      const latestAgreementByStylist = {};
      (agreements || []).forEach(a => {
        if (!latestAgreementByStylist[a.stylist_id]) {
          latestAgreementByStylist[a.stylist_id] = a;
        }
      });

      const complianceByStylist = {};
      (compliance || []).forEach(c => {
        complianceByStylist[c.stylist_id] = c;
      });

      const combined = (stylists || []).map(p => ({
        stylist: p,
        agreement: latestAgreementByStylist[p.id] || null,
        compliance: complianceByStylist[p.id] || null,
      }));

      setRows(combined);
    } catch (err) {
      console.error('LegalCompliancePage fetch error:', err);
      showSnack(`Failed to load legal data: ${String(err?.message || 'Unknown error')}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLegal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredRows = useMemo(() => {
    let list = rows;

    if (statusFilter !== 'all') {
      list = list.filter(({ agreement }) => {
        if (statusFilter === 'not_sent') return !agreement;
        return agreement?.status === statusFilter;
      });
    }

    const term = search.trim().toLowerCase();
    if (!term) return list;

    return list.filter(({ stylist: p, agreement: r }) => {
      const hay = [
        r?.agreement_type,
        r?.agreement_version,
        r?.status,
        p.first_name,
        p.last_name,
        p.email,
        p.phone_number,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(term);
    });
  }, [rows, statusFilter, search]);

  const sendAgreementTo = async (stylistId) => {
    setSendingFor(stylistId);
    try {
      const { error } = await supabase.functions.invoke('stylist-legal-agreement', {
        body: { action: 'send', stylist_id: stylistId },
      });
      if (error) throw error;
      showSnack('Agreement sent.');
      fetchLegal();
    } catch (err) {
      console.error('Send agreement failed:', err);
      // supabase-js's FunctionsHttpError.message is just a generic "non-2xx"
      // string — the function's actual error reason is in the response
      // body, reachable via error.context (the raw Response object).
      let detail = err.message;
      try {
        const body = await err.context?.json();
        if (body?.error) detail = body.error;
      } catch (_) {
        // context wasn't JSON or wasn't readable — fall back to err.message
      }
      showSnack(`Send failed: ${detail}`, 'error');
    } finally {
      setSendingFor(null);
    }
  };

  const openTemplateEditor = async () => {
    setTemplateOpen(true);
    setTemplateLoading(true);
    setTemplateFile(null);
    setTemplateClearPdf(false);
    try {
      const { data, error } = await supabase.functions.invoke('stylist-legal-agreement', {
        body: { action: 'get_template', agreement_type: AGREEMENT_TYPE },
      });
      if (error) throw error;
      setTemplateVersion(data?.version || '1.0');
      setTemplateBodyText(data?.body_text || '');
      setTemplatePdfUrl(data?.template_pdf_url || null);
    } catch (err) {
      console.error('Failed to load agreement template:', err);
      let detail = err.message;
      try {
        const body = await err.context?.json();
        if (body?.error) detail = body.error;
      } catch (_) { /* fall back to err.message */ }
      showSnack(`Failed to load template: ${detail}`, 'error');
      setTemplateOpen(false);
    } finally {
      setTemplateLoading(false);
    }
  };

  const saveTemplate = async () => {
    setTemplateSaving(true);
    try {
      const payload = {
        action: 'update_template',
        agreement_type: AGREEMENT_TYPE,
        version: templateVersion,
        body_text: templateBodyText,
      };
      if (templateFile) {
        payload.template_pdf_base64 = await fileToBase64(templateFile);
      } else if (templateClearPdf) {
        payload.clear_template_pdf = true;
      }
      const { error } = await supabase.functions.invoke('stylist-legal-agreement', { body: payload });
      if (error) throw error;
      showSnack('Agreement template saved. New sends/signatures will use it.');
      setTemplateOpen(false);
      setTemplateFile(null);
      setTemplateClearPdf(false);
    } catch (err) {
      console.error('Failed to save agreement template:', err);
      let detail = err.message;
      try {
        const body = await err.context?.json();
        if (body?.error) detail = body.error;
      } catch (_) { /* fall back to err.message */ }
      showSnack(`Save failed: ${detail}`, 'error');
    } finally {
      setTemplateSaving(false);
    }
  };

  const toggleRow = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const runBulkAction = async (action) => {
    const caseIds = Array.from(selected);
    if (caseIds.length === 0) {
      showSnack('Select at least one row first.', 'warning');
      return;
    }

    try {
      const { error } = await supabase.functions.invoke('admin-legal-bulk-action', {
        body: { action, case_ids: caseIds },
      });
      if (error) throw error;
      showSnack(`Action '${action}' completed for ${caseIds.length} row(s).`);
      setSelected(new Set());
      fetchLegal();
    } catch (err) {
      console.error('Bulk legal action failed:', err);
      showSnack(`Bulk action failed: ${err.message}`, 'error');
    }
  };

  const exportCSV = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('admin-legal-export');
      if (error) throw error;

      const csv = data?.csv || '';
      if (!csv) {
        showSnack('CSV export returned no data.', 'warning');
        return;
      }

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `legal-compliance-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showSnack(`CSV exported (${data?.row_count ?? 'unknown'} rows).`);
    } catch (err) {
      console.error('Legal export failed:', err);
      showSnack(`Export failed: ${err.message}`, 'error');
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
        Legal Compliance
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        {/* Filters and actions on separate rows. They were one unwrapping row of
            six controls, so the last button ran off the edge at any real width. */}
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            label="Search stylist, email, phone or agreement"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 240 }}
          />
          <TextField
            select
            label="Status"
            size="small"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            {STATUS_OPTIONS.map(opt => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </TextField>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Button size="small" startIcon={<RefreshIcon />} onClick={fetchLegal}>Refresh</Button>
            <Button size="small" startIcon={<DownloadIcon />} variant="outlined" onClick={exportCSV}>Export CSV</Button>
            <Button size="small" variant="outlined" onClick={() => runBulkAction('void_reissue')}>
              Void / reissue selected
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button size="small" startIcon={<DescriptionIcon />} variant="contained" onClick={openTemplateEditor}>
              Edit agreement contract
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Dialog open={templateOpen} onClose={() => (!templateSaving ? setTemplateOpen(false) : null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          Independent Contractor Agreement
          <IconButton
            onClick={() => setTemplateOpen(false)}
            disabled={templateSaving}
            sx={{ ml: 'auto' }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {templateLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                This is the agreement text every stylist reviews and signs in-app. Edits here apply to
                new sends and signatures immediately — already-signed PDFs are unaffected.
              </Typography>
              <TextField
                label="Version"
                size="small"
                value={templateVersion}
                onChange={(e) => setTemplateVersion(e.target.value)}
                sx={{ maxWidth: 160 }}
              />
              <TextField
                label="Agreement text"
                multiline
                minRows={10}
                value={templateBodyText}
                onChange={(e) => setTemplateBodyText(e.target.value)}
                helperText="Used to generate the contract PDF when no replacement PDF below is uploaded."
              />

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Replace with an uploaded PDF (optional)</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  If uploaded, stylists see and sign this exact PDF instead of the generated text above —
                  a signature certificate page is appended automatically when they sign.
                </Typography>
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                  {templatePdfUrl && !templateClearPdf ? (
                    <Button size="small" href={templatePdfUrl} target="_blank" rel="noreferrer">
                      View Current PDF
                    </Button>
                  ) : null}
                  <Button component="label" size="small" variant="outlined" startIcon={<UploadFileIcon />}>
                    {templateFile ? templateFile.name : 'Upload Replacement PDF'}
                    <input
                      type="file"
                      accept="application/pdf"
                      hidden
                      onChange={(e) => {
                        setTemplateFile(e.target.files?.[0] || null);
                        setTemplateClearPdf(false);
                      }}
                    />
                  </Button>
                  {templatePdfUrl && !templateFile ? (
                    <Button
                      size="small"
                      color="warning"
                      onClick={() => setTemplateClearPdf(v => !v)}
                    >
                      {templateClearPdf ? 'Will remove on save — undo' : 'Remove uploaded PDF'}
                    </Button>
                  ) : null}
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateOpen(false)} disabled={templateSaving}>Cancel</Button>
          <Button variant="contained" onClick={saveTemplate} disabled={templateSaving || templateLoading}>
            {templateSaving ? 'Saving…' : 'Save Template'}
          </Button>
        </DialogActions>
      </Dialog>

      <Paper sx={{ p: 1.5 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : filteredRows.length === 0 ? (
          <Typography sx={{ p: 2 }} color="text.secondary">No legal rows found.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {filteredRows.map(({ stylist: p, agreement: r, compliance: c }) => {
              const statusLabel = prettyStatus(r ? r.status : 'not sent');
              const statusColor = !r ? 'default' : r.status === 'signed' ? 'success' : r.status === 'failed' ? 'error' : 'warning';
              return (
                <Paper key={p.id} variant="outlined" sx={{ p: 2 }}>
                  {/* Two tiers: who and what on top, state underneath. Nine
                      items on one line is what made this unreadable. */}
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={r ? selected.has(r.id) : false}
                          onChange={() => r && toggleRow(r.id)}
                          disabled={!r}
                        />
                      }
                      label=""
                      sx={{ mr: 0 }}
                    />
                    {/* minWidth: 0 lets a long email truncate instead of
                        pushing the action button off the row. */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600 }} noWrap>
                        {`${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Unnamed stylist'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {p.email || 'No email'} • {p.phone_number || 'No phone'}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.75 }}>
                        {r ? prettyStatus(r.agreement_type) : 'No agreement on file'}
                        {r ? (
                          <Typography component="span" variant="body2" color="text.secondary">
                            {` · v${r.agreement_version || '-'} · ${prettyStatus(r.provider) || '-'}`}
                          </Typography>
                        ) : null}
                      </Typography>
                    </Box>

                    <Stack spacing={0.75} alignItems="flex-end" sx={{ flexShrink: 0 }}>
                      {!r || r.status !== 'signed' ? (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={sendingFor === p.id}
                        onClick={() => sendAgreementTo(p.id)}
                      >
                        {sendingFor === p.id ? 'Sending…' : (r ? 'Resend' : 'Send agreement')}
                      </Button>
                      ) : null}
                      {r?.signed_pdf_path ? (
                        <Button size="small" onClick={() => openLegalDoc(r.signed_pdf_path)}>
                          View PDF
                        </Button>
                      ) : null}
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {r?.signed_at ? `Signed ${new Date(r.signed_at).toLocaleDateString()}` : 'Not signed'}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.25, pl: 5 }}>
                    <Chip label={statusLabel} size="small" color={statusColor} />
                    <Chip label={`Agreement: ${prettyStatus(c?.agreement_status) || '—'}`} size="small"
                      color={c?.agreement_status === 'signed' ? 'success' : 'info'} variant="outlined" />
                    <Chip label={`Identity: ${prettyStatus(c?.identity_status) || '—'}`} size="small"
                      color={c?.identity_status === 'verified' ? 'success' : 'default'} variant="outlined" />
                    {/* Amber regardless of value read as a warning even when the
                        stylist was clear to be paid. */}
                    <Chip label={`Payout: ${prettyStatus(c?.payout_status) || '—'}`} size="small"
                      color={c?.payout_status === 'eligible' ? 'success'
                        : c?.payout_status ? 'warning' : 'default'} variant="outlined" />
                  </Stack>
                </Paper>
              );
            })}
          </Box>
        )}
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} sx={{ width: '100%' }}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
